import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reportsService } from '../../../services/reportsService';
import { useAuth } from '../../../contexts/AuthContext';

const RecentReportsList = ({ onReportSelect, onViewAllReports }) => {
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user, organizationId } = useAuth();

  useEffect(() => {
    if (user && organizationId) {
      loadRecentReports();
    }
  }, [user, organizationId]);

  const loadRecentReports = async () => {
    if (!user || !organizationId) return;
    
    setLoading(true);
    setError('');
    
    try {
      if (!organizationId) {
        throw new Error('No organization ID found for user. Please check your account setup.');
      }
      
      const reports = await reportsService?.getRecentReports(organizationId);
      setRecentReports(reports);
    } catch (error) {
      console.error('Failed to load recent reports:', error);
      setError('Failed to load recent reports');
      setRecentReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReportClick = (report) => {
    onReportSelect?.(report);
  };

  const handleViewAllClick = () => {
    onViewAllReports?.();
  };

  const getReportIcon = (type) => {
    switch (type) {
      case 'revenue':
        return 'TrendingUp';
      case 'expenses':
        return 'ArrowDown';
      case 'cash-flow':
        return 'ArrowRightLeft';
      case 'financial':
        return 'FileText';
      default:
        return 'BarChart3';
    }
  };

  const getReportColor = (type) => {
    switch (type) {
      case 'revenue':
        return 'text-success';
      case 'expenses':
        return 'text-error';
      case 'cash-flow':
        return 'text-primary';
      case 'financial':
        return 'text-accent';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never accessed';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date?.getFullYear() !== now?.getFullYear() ? 'numeric' : undefined
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Recent Reports</h3>
          <div className="w-20 h-4 bg-muted/50 rounded animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[...Array(3)]?.map((_, index) => (
            <div key={index} className="flex items-center space-x-4 animate-pulse">
              <div className="w-10 h-10 bg-muted/50 rounded-lg"></div>
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-muted/50 rounded"></div>
                <div className="w-1/2 h-3 bg-muted/50 rounded"></div>
              </div>
              <div className="w-16 h-3 bg-muted/50 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-error/20 rounded-lg p-6">
        <div className="text-center">
          <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-3" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Reports</h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button 
            variant="outline" 
            size="sm"
            onClick={loadRecentReports}
            iconName="RefreshCw"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Recent Reports</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          iconName="ArrowRight"
          className="text-primary hover:text-primary/80"
        >
          View All
        </Button>
      </div>

      {/* Recent Reports List */}
      {recentReports && recentReports?.length > 0 ? (
        <div className="space-y-4">
          {recentReports?.map((report) => (
            <div
              key={report?.id}
              className="flex items-center space-x-4 p-3 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors duration-150"
              onClick={() => handleReportClick(report)}
            >
              {/* Report Icon */}
              <div className={`w-10 h-10 rounded-lg bg-muted/20 flex items-center justify-center ${getReportColor(report?.type)}`}>
                <Icon name={getReportIcon(report?.type)} size={20} />
              </div>

              {/* Report Info */}
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-foreground truncate">
                  {report?.name}
                </h4>
                <p className="text-xs text-muted-foreground truncate">
                  {report?.description}
                </p>
              </div>

              {/* Last Accessed */}
              <div className="flex flex-col items-end">
                <div className="text-xs text-muted-foreground">
                  {formatDate(report?.lastAccessed)}
                </div>
                <Icon name="ChevronRight" size={14} className="text-muted-foreground mt-1" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Recent Reports</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Start by creating your first report or accessing available report categories.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewAllClick}
            iconName="Plus"
          >
            Browse Reports
          </Button>
        </div>
      )}

      {/* Quick Actions */}
      {recentReports && recentReports?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadRecentReports}
              iconName="RefreshCw"
              className="flex-1"
            >
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAllClick}
              iconName="Grid3X3"
              className="flex-1"
            >
              Categories
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentReportsList;